class MainProductSecondary extends HTMLElement {
  constructor() {
    super();

    this.variantSelector = null;
    this.buyButton = null;
    this.productQuantity = null;
    this.slider = null;
    this.quantity = 1;
    this.selectedVariantId = null;
    this.currentSelectedColor = null;
    this.isUpdatingFromColorChange = false;
    this.isUpdatingImages = false;
    this.currentImageFetchController = null;
    this.currentDropdownUpdateController = null;
    
    this.productDataCache = null;
    this.productHandleCache = null;
    this.variantChangeDebounceTimer = null;
    this.pendingImageUpdates = new Set();
    
    this.activeDropdownSelections = {};
  }

  connectedCallback() {
    this.variantSelector = this.querySelector('variant-selector-secondary');
    this.buyButton = this.querySelector('buy-button');
    this.productQuantity = this.querySelector('product-quantity');
    this.slider = this.querySelector('main-product-secondary-slider');
    this.selectedVariantId = this.dataset.productId;

    this.initializeCurrentColor();
    this.initVariantListener();
    this.initQuantityListener();
    this.checkZeptoAndShowSection();
  }

  initializeCurrentColor() {
    const variantSelector = this.querySelector('variant-selector-secondary');
    if (!variantSelector) return;
    
    const colorOptionIndex = this.findColorOptionIndex(variantSelector);
    const selectedColorInput = variantSelector.querySelector(`[data-variant-head]:checked[data-head-position="${colorOptionIndex}"]`);
    if (selectedColorInput) {
      this.currentSelectedColor = selectedColorInput.dataset.variantHead;
    } else {
      const checkedOption = variantSelector.querySelector('[data-variant-option]:checked');
      if (checkedOption) {
        const optionWay = checkedOption.dataset.variantOption;
        if (optionWay) {
          const optionWayArray = optionWay.split(',');
          this.currentSelectedColor = optionWayArray[colorOptionIndex];
        }
      }
    }
  }

  initVariantListener() {
    if (this.variantSelector) {
      this.variantSelector.addEventListener('variant-change', (event) => {
        if (this.variantChangeDebounceTimer) {
          clearTimeout(this.variantChangeDebounceTimer);
        }
        
        this.variantChangeDebounceTimer = setTimeout(() => {
          this.handleVariantChange(event);
        }, 50);
      });
      
      this.variantSelector.addEventListener('color-change', (event) => {
        if (event.detail.color) {
          this.isUpdatingFromColorChange = true;
          this.currentSelectedColor = event.detail.color;
          this.setDropdownsDisabled(true);
          
          const variantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
          variantDropdowns.forEach((dropdown) => {
            const optionIndex = parseInt(dropdown.dataset.optionIndex);
            const selectedOption = dropdown.querySelector('.variant-dropdown__option.selected');
            if (selectedOption) {
              this.activeDropdownSelections[optionIndex] = selectedOption.dataset.value;
            }
          });
          
          this.updateVariantDropdownOptions(event.detail.color);
        }
      });
    }
    
    const variantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
    variantDropdowns.forEach((dropdown) => {
      dropdown.addEventListener('dropdown-option-selected', (event) => {
        const { variantId, optionIndex, value } = event.detail;
        
        if (value && optionIndex !== undefined) {
          this.activeDropdownSelections[optionIndex] = value;
        } else if (optionIndex !== undefined) {
          delete this.activeDropdownSelections[optionIndex];
        }
        
        setTimeout(async () => {
          if (variantId) {
            await this.findAndUpdateVariant(variantId, optionIndex);
          } else {
            await this.findAndUpdateVariant(null, optionIndex);
          }
        }, 0);
      });
    });
    
    this.initializeButtonPrice();
    this.initializePersonalizerButtonPrice();
  }

  handleVariantChange(event) {
    const variantId = event.detail.variantId;
    const priceData = event.detail.price;
    
    const variantSelector = this.querySelector('variant-selector-secondary');
    const colorOptionIndex = this.findColorOptionIndex(variantSelector);
    const checkedOption = variantSelector?.querySelector(`[data-variant-option][value="${variantId}"]`);
    const optionWay = checkedOption?.dataset.variantOption;
    const newSelectedColor = optionWay ? optionWay.split(',')[colorOptionIndex] : null;
    
    const colorChanged = newSelectedColor && newSelectedColor !== this.currentSelectedColor;
    
    this.selectedVariantId = variantId;
    if (newSelectedColor) {
      this.currentSelectedColor = newSelectedColor;
    }
    
    if (this.buyButton) {
      this.buyButton.changeVariantId(variantId);
      if (priceData?.price) {
        this.buyButton.updateButtonText(priceData.price);
      }
    }

    if (priceData?.price) {
      this.updatePrice(priceData);
    } else if (variantId) {
      const variantOption = this.querySelector(`[data-variant-option][value="${variantId}"]`);
      if (variantOption?.dataset.price) {
        this.updatePrice({
          price: variantOption.dataset.price,
          comparePrice: variantOption.dataset.comparePrice || ''
        });
      }
    }
    
    if (colorChanged || this.isUpdatingFromColorChange) {
      this.setDropdownsDisabled(true);
      this.updateAllDropdownImages(variantId).finally(() => {
        this.isUpdatingFromColorChange = false;
      });
    }
    
    if (!this.pendingImageUpdates.has(variantId)) {
      this.pendingImageUpdates.add(variantId);
      requestAnimationFrame(() => {
        this.updateVariantImages(variantId).finally(() => {
          this.pendingImageUpdates.delete(variantId);
        });
      });
    }
    
    this.isUpdatingFromColorChange = false;
  }

  async findAndUpdateVariant(selectedVariantId, selectedOptionIndex) {
    const variantSelector = this.querySelector('variant-selector-secondary');
    if (!variantSelector) {
      return;
    }
    
    const colorOptionIndex = this.findColorOptionIndex(variantSelector);
    const selectedColorInput = variantSelector.querySelector(`[data-variant-head]:checked[data-head-position="${colorOptionIndex}"]`);
    let selectedColor = selectedColorInput ? selectedColorInput.dataset.variantHead : null;
    
    if (!selectedColor) {
      const firstCheckedColor = variantSelector.querySelector('[data-variant-head]:checked');
      if (firstCheckedColor) {
        selectedColor = firstCheckedColor.dataset.variantHead;
      }
    }
    
    if (!selectedColor && this.currentSelectedColor) {
      selectedColor = this.currentSelectedColor;
    }
    
    const allVariantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
    const variantDropdowns = Array.from(allVariantDropdowns).filter(dropdown => {
      const optionIndex = dropdown.dataset.optionIndex;
      const parsedIndex = parseInt(optionIndex);
      return !isNaN(parsedIndex) && optionIndex !== undefined && optionIndex !== null;
    });
    
    const selectedValues = { ...this.activeDropdownSelections };
    
    variantDropdowns.forEach((dropdown) => {
      const optionIndexRaw = dropdown.dataset.optionIndex;
      const optionIndex = parseInt(optionIndexRaw);
      
      if (isNaN(optionIndex)) return;
      
      const selectedOption = dropdown.querySelector('.variant-dropdown__option.selected');
      if (selectedOption) {
        const optionValue = selectedOption.dataset.value;
        selectedValues[optionIndex] = optionValue;
        this.activeDropdownSelections[optionIndex] = optionValue;
      }
    });
    
    if (!selectedVariantId && selectedOptionIndex !== undefined && selectedOptionIndex !== null) {
      delete selectedValues[selectedOptionIndex];
      delete this.activeDropdownSelections[selectedOptionIndex];
    }
    
    if (Object.keys(selectedValues).length === 0 && !selectedVariantId) {
      const allOptions = Array.from(variantSelector.querySelectorAll('[data-variant-option]')).filter((option) => {
        const optionWay = option.dataset.variantOption;
        if (!optionWay) return false;
        const optionWayArray = optionWay.split(',');
        if (!selectedColor) return true;
        return optionWayArray[colorOptionIndex] === selectedColor;
      });
      
      if (allOptions.length > 0) {
        const firstOption = allOptions[0];
        firstOption.checked = true;
        variantSelector.selectedVariant = firstOption.value;
        variantSelector.eventDispatch();
      }
      return;
    }
    
    const allOptions = Array.from(variantSelector.querySelectorAll('[data-variant-option]')).filter((option) => {
      const optionWay = option.dataset.variantOption;
      if (!optionWay) return false;
      const optionWayArray = optionWay.split(',');
      if (!selectedColor) return true;
      return optionWayArray[colorOptionIndex] === selectedColor;
    });
    
    const productHandle = this.slider?.dataset.productHandle || 
                         this.dataset.productHandle ||
                         window.location.pathname.split('/products/')[1]?.split('?')[0];
    
    let matchingOptions = [];
    
    if (productHandle && Object.keys(selectedValues).length > 0) {
      try {
        const productData = await this.getProductData(productHandle, null, false);
        
        if (productData && productData.variants) {
          const matchingVariants = productData.variants.filter((variant) => {
            const variantOptions = variant.options || [];
            
            if (selectedColor) {
              const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                                (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                                variantOptions[0] === selectedColor || 
                                variantOptions[1] === selectedColor;
              
              if (!colorMatch) return false;
            }
            
            let matchesAll = true;
            Object.keys(selectedValues).forEach((index) => {
              const idx = parseInt(index);
              if (variantOptions[idx] !== selectedValues[index]) {
                matchesAll = false;
              }
            });
            
            return matchesAll;
          });
          
          if (matchingVariants.length > 0) {
            const matchingVariantIds = matchingVariants.map(v => v.id.toString());
            matchingOptions = allOptions.filter(option => 
              matchingVariantIds.includes(option.value)
            );
          }
        }
      } catch (error) {
      }
    }
    
    if (matchingOptions.length === 0) {
      matchingOptions = allOptions.filter((option) => {
        const optionWay = option.dataset.variantOption;
        if (!optionWay) return false;
        const optionWayArray = optionWay.split(',');
        
        if (selectedColor && colorOptionIndex < optionWayArray.length) {
          if (optionWayArray[colorOptionIndex] !== selectedColor) {
            return false;
          }
        }
        
        let matchesAll = true;
        
        Object.keys(selectedValues).forEach((index) => {
          const idx = parseInt(index);  
          if (idx < optionWayArray.length) {
            if (optionWayArray[idx] !== selectedValues[index]) {
              matchesAll = false;
            }
          } else {
            matchesAll = false;
          }
        });
        
        return matchesAll;
      });
    }
    
    if (matchingOptions.length > 0) {
      const matchingOption = matchingOptions[0];
      if (matchingOption.value !== variantSelector.selectedVariant || !matchingOption.checked) {
        matchingOption.checked = true;
        variantSelector.selectedVariant = matchingOption.value;
        variantSelector.eventDispatch();
      }
      return;
    }
    
    if (selectedVariantId) {
      const checkedOption = variantSelector.querySelector(`[data-variant-option][value="${selectedVariantId}"]`);
      if (checkedOption) {
        const optionWay = checkedOption.dataset.variantOption;
        if (optionWay) {
          const optionWayArray = optionWay.split(',');
          const colorMatches = !selectedColor || (colorOptionIndex < optionWayArray.length && optionWayArray[colorOptionIndex] === selectedColor);
          
          if (colorMatches && checkedOption.value !== variantSelector.selectedVariant) {
            checkedOption.checked = true;
            variantSelector.selectedVariant = selectedVariantId;
            variantSelector.eventDispatch();
          }
        }
      }
      return;
    }
  }

  initializeButtonPrice() {
    if (!this.buyButton) return;
    
    const priceElement = this.querySelector('.product-title-info__price .product-price__current');
    if (priceElement && priceElement.textContent) {
      this.buyButton.updateButtonText(priceElement.textContent.trim());
    } else {
      const checkedOption = this.querySelector('[data-variant-option]:checked');
      if (checkedOption && checkedOption.dataset.price) {
        this.buyButton.updateButtonText(checkedOption.dataset.price);
      }
    }
  }

  initializePersonalizerButtonPrice() {
    const personalizerButton = this.querySelector('.product-personalizer button.pplr-c-button, .product-personalizer button.pplr-btn');
    if (personalizerButton) {
      this.updatePersonalizerButtonText(personalizerButton);
    } else {
      const observer = new MutationObserver(() => {
        const button = this.querySelector('.product-personalizer button.pplr-c-button, .product-personalizer button.pplr-btn');
        if (button) {
          this.updatePersonalizerButtonText(button);
          observer.disconnect();
        }
      });
      
      const personalizerContainer = this.querySelector('.product-personalizer');
      if (personalizerContainer) {
        observer.observe(personalizerContainer, { childList: true, subtree: true });
        
        setTimeout(() => {
          observer.disconnect();
        }, 5000);
      }
    }
  }

  updatePersonalizerButtonText(button) {
    if (!button) {
      button = this.querySelector('.product-personalizer button.pplr-c-button, .product-personalizer button.pplr-btn');
    }
    if (!button) return;
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        let currentCurrency = 'GBP'; 
        if (cart && cart.currency) {
          currentCurrency = cart.currency.toUpperCase();
        } else {
          const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
          if (currencyMeta) {
            const currencyCode = currencyMeta.getAttribute('content');
            if (currencyCode) {
              currentCurrency = currencyCode.toUpperCase();
            }
          }
        }
        
        this.updateButtonPriceText(button, currentCurrency);
      })
      .catch(() => {
        const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
        let currentCurrency = 'GBP'; 
        if (currencyMeta) {
          const currencyCode = currencyMeta.getAttribute('content');
          if (currencyCode) {
            currentCurrency = currencyCode.toUpperCase();
          }
        }
        this.updateButtonPriceText(button, currentCurrency);
      });
  }

  updateButtonPriceText(button, currency) {
    const baseText = 'ADD CUSTOM SOLE MESSAGE';
    let priceText = '';
    
    if (currency === 'USD' || currency === 'US') {
      priceText = '$118';
    } else {
      priceText = '£90';
    }
    
    button.textContent = `${baseText} - ${priceText}`;
  }

  initQuantityListener() {
    if (this.productQuantity) {
      this.productQuantity.addEventListener('quantity-change', (event) => {
        this.quantity = event.detail.quantity;
        
        if (this.buyButton) {
          this.buyButton.dataset.quantity = this.quantity;
        }
      });
    }
  }

  checkZeptoAndShowSection() {
    const customSoleMessage = this.querySelector('[data-custom-sole-message]');
    if (!customSoleMessage) return;
    
    const checkAndShow = () => {
      const personalizerContainer = document.querySelector('.product-personalizer');
      
      if (personalizerContainer) {
        const hasButton = personalizerContainer.querySelector('button.pplr-c-button, button.pplr-btn');
        if (hasButton) {
          customSoleMessage.style.display = 'block';
          return true;
        }
      }
      
      customSoleMessage.style.display = 'none';
      return false;
    };
    
    if (checkAndShow()) return;
    
    requestAnimationFrame(() => {
      if (checkAndShow()) return;
      
      setTimeout(() => {
        if (checkAndShow()) return;
        
        const observer = new MutationObserver(() => {
          if (checkAndShow()) {
            observer.disconnect();
          }
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
        
        const productContainer = this.closest('main-product-secondary');
        if (productContainer) {
          observer.observe(productContainer, { childList: true, subtree: true });
        }
        
        let checkCount = 0;
        const intervalCheck = setInterval(() => {
          checkCount++;
          if (checkAndShow() || checkCount >= 50) {
            clearInterval(intervalCheck);
            observer.disconnect();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(intervalCheck);
          observer.disconnect();
          checkAndShow();
        }, 5000);
      }, 50);
    });
  }

  updatePrice(priceData) {
    if (!priceData?.price) {
      return;
    }
    
    const extractText = (htmlString) => {
      if (!htmlString) return '';
      const temp = document.createElement('div');
      temp.innerHTML = htmlString;
      return temp.textContent || temp.innerText || '';
    };
    
    const cleanPrice = extractText(priceData.price);
    const cleanComparePrice = priceData.comparePrice ? extractText(priceData.comparePrice) : '';
    
    const titlePriceElements = Array.from(new Set([
      ...this.querySelectorAll('.product-title-info__price'),
      ...document.querySelectorAll('.product-title-info__price')
    ]));
    
    titlePriceElements.forEach((titlePriceElement) => {
      const priceCurrent = titlePriceElement.querySelector('.product-price__current');
      const priceCompare = titlePriceElement.querySelector('.product-price__compare');
      
      if (!priceCurrent) return;
      
      priceCurrent.textContent = cleanPrice;
      
      if (cleanComparePrice && cleanComparePrice !== cleanPrice) {
        if (!priceCompare) {
          const compareSpan = document.createElement('span');
          compareSpan.className = 'product-price__compare';
          const sTag = document.createElement('s');
          sTag.textContent = cleanComparePrice;
          compareSpan.appendChild(sTag);
          titlePriceElement.insertBefore(compareSpan, priceCurrent);
        } else {
          const sTag = priceCompare.querySelector('s') || document.createElement('s');
          sTag.textContent = cleanComparePrice;
          if (!priceCompare.querySelector('s')) {
            priceCompare.appendChild(sTag);
          }
        }
        priceCurrent.classList.add('on-sale');
      } else {
        priceCompare?.remove();
        priceCurrent.classList.remove('on-sale');
      }
    });
    
    const priceElement = this.querySelector('.product-price');
    if (priceElement) {
      const discountedPrice = priceElement.querySelector('.discounted-price');
      const originalPrice = priceElement.querySelector('.original-price');
      if (discountedPrice && priceData.price) {
        discountedPrice.textContent = extractText(priceData.price);
      }
      if (originalPrice && priceData.comparePrice) {
        originalPrice.textContent = extractText(priceData.comparePrice);
      }
    }

    const installmentInfo = this.querySelector('.info_installment');
    if (installmentInfo && priceData.price) {
      installmentInfo.dataset.price = extractText(priceData.price);
      if (typeof calculateInstallments === 'function') {
        calculateInstallments(installmentInfo);
      }
    }
  }

  async updateVariantDropdownOptions(selectedColor) {
    if (this.currentDropdownUpdateController) {
      this.currentDropdownUpdateController.abort();
    }
    
    this.currentDropdownUpdateController = new AbortController();
    const signal = this.currentDropdownUpdateController.signal;
    
    try {
      const productHandle = this.slider?.dataset.productHandle || 
                           this.dataset.productHandle ||
                           window.location.pathname.split('/products/')[1]?.split('?')[0];
      
      if (!productHandle) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const allVariantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
      const variantDropdowns = Array.from(allVariantDropdowns).filter(dropdown => {
        const optionIndex = dropdown.dataset.optionIndex;
        const parsedIndex = parseInt(optionIndex);
        return !isNaN(parsedIndex) && optionIndex !== undefined && optionIndex !== null;
      });
      
      if (variantDropdowns.length === 0) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const variantSelector = this.querySelector('variant-selector-secondary');
      if (!variantSelector) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const colorOptionIndex = this.findColorOptionIndex(variantSelector);
      
      const selectedValues = { ...this.activeDropdownSelections };
      
      variantDropdowns.forEach((dropdown) => {
        const optionIndex = parseInt(dropdown.dataset.optionIndex);
        const selectedOption = dropdown.querySelector('.variant-dropdown__option.selected');
        if (selectedOption) {
          selectedValues[optionIndex] = selectedOption.dataset.value;
          this.activeDropdownSelections[optionIndex] = selectedOption.dataset.value;
        }
      });
      
      const productData = await this.getProductData(productHandle, signal);
      
      if (!productData || signal.aborted) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const baseProductPrice = productData.price_min || productData.variants[0]?.price || 0;
      
      const formatMoney = (cents) => {
        if (!cents || cents === 0) return '';
        let formatted = '';
        if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
          formatted = theme.Currency.formatMoney(cents, theme.settings?.moneyFormat);
        } else {
          const amount = (cents / 100).toFixed(2);
          const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
          const currencyCode = currencyMeta?.getAttribute('content') || 'GBP';
          const currencySymbol = currencyCode === 'USD' ? '$' : '£';
          formatted = currencySymbol + amount;
        }
        if (formatted.includes('<')) {
          const temp = document.createElement('div');
          temp.innerHTML = formatted;
          return temp.textContent || temp.innerText || formatted;
        }
        return formatted;
      };
      
      const variantDataMap = new Map();
      const formatImageUrl = (image) => {
        if (!image) return null;
        const url = typeof image === 'string' ? image : (image.src || image);
        if (!url) return null;
        if (url.startsWith('//')) return 'https:' + url;
        if (url.startsWith('/')) return window.location.origin + url;
        return url;
      };
      
      productData.variants.forEach((variant) => {
        const variantOptions = variant.options || [];
        const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                          (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                          variantOptions[0] === selectedColor || 
                          variantOptions[1] === selectedColor;
        
        if (colorMatch) {
          const variantId = variant.id.toString();
          const variantImage = variant.featured_image 
            ? formatImageUrl(variant.featured_image)
            : (variant.featured_media?.preview_image ? formatImageUrl(variant.featured_media.preview_image) : null);
          
          const priceDifference = variant.price - baseProductPrice;
          const formattedPriceDiff = priceDifference > 0 ? formatMoney(priceDifference) : '';
          
          variantDataMap.set(variantId, {
            image: variantImage,
            price: variant.price || 0,
            priceDifference: priceDifference,
            formattedPriceDiff: formattedPriceDiff,
            comparePrice: variant.compare_at_price || 0
          });
        }
      });
      
      variantDropdowns.forEach((dropdown) => {
        const optionIndex = parseInt(dropdown.dataset.optionIndex) || 0;
        const options = dropdown.querySelectorAll('.variant-dropdown__option');
        
        options.forEach((option) => {
          const optionValue = option.dataset.value;
          if (!optionValue) return;
          
          const variantIdWithContext = this.findVariantIdForOptionWithContext(
            productData.variants,
            selectedColor,
            colorOptionIndex,
            optionIndex,
            optionValue,
            selectedValues
          );
          
          const variantIdForImage = this.findVariantIdForOption(
            productData.variants,
            selectedColor,
            colorOptionIndex,
            optionIndex,
            optionValue
          );
          
          if (!variantIdWithContext) return;
          
          option.dataset.variantId = variantIdWithContext;
          
          const basePricesForOption = [];
          productData.variants.forEach((variant) => {
            const variantOptions = variant.options || [];
            const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                              (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                              variantOptions[0] === selectedColor || 
                              variantOptions[1] === selectedColor;
            
            if (colorMatch) {
              if (variantOptions[optionIndex] === optionValue) {
                return; 
              }
              
              let matchesPreviousSelections = true;
              Object.keys(selectedValues).forEach((idx) => {
                const index = parseInt(idx);
                if (index !== optionIndex && index !== colorOptionIndex) {
                  if (variantOptions[index] !== selectedValues[index]) {
                    matchesPreviousSelections = false;
                  }
                }
              });
              
              if (matchesPreviousSelections) {
                basePricesForOption.push(variant.price);
              }
            }
          });
          const minPriceForOption = basePricesForOption.length > 0 ? Math.min(...basePricesForOption) : baseProductPrice;
          
          const variantDataForPrice = variantDataMap.get(variantIdWithContext);
          if (variantDataForPrice) {
            const formattedPrice = formatMoney(variantDataForPrice.price);
            if (formattedPrice) option.dataset.price = formattedPrice;
            if (variantDataForPrice.comparePrice) {
              const formattedComparePrice = formatMoney(variantDataForPrice.comparePrice);
              option.dataset.comparePrice = formattedComparePrice;
            }
            
            const incrementalDifference = variantDataForPrice.price - minPriceForOption;
            const formattedIncrementalDiff = incrementalDifference > 0 ? formatMoney(incrementalDifference) : '';
            
            const priceElement = option.querySelector('.variant-dropdown__option-price');
            if (priceElement) {
              if (incrementalDifference > 0 && formattedIncrementalDiff) {
                priceElement.textContent = '+' + formattedIncrementalDiff;
                priceElement.style.display = '';
              } else {
                priceElement.style.display = 'none';
              }
            }
          }
          
          if (variantIdForImage) {
            const variantDataForImage = variantDataMap.get(variantIdForImage);
            if (variantDataForImage && variantDataForImage.image) {
              let imageUrl = variantDataForImage.image;
              if (!imageUrl.includes('width=')) {
                imageUrl = imageUrl.replace(/\?.*$/, '') + '?width=73';
              }
              
              const imageContainer = option.querySelector('.variant-dropdown__option-image');
              if (imageContainer) {
                let currentImage = imageContainer.querySelector('img');
                if (!currentImage) {
                  currentImage = document.createElement('img');
                  currentImage.width = 73;
                  currentImage.height = 60;
                  currentImage.alt = optionValue;
                  imageContainer.appendChild(currentImage);
                }
                if (currentImage.src !== imageUrl) {
                  currentImage.src = imageUrl;
                  currentImage.alt = optionValue;
                }
              } else {
                const newImageContainer = document.createElement('div');
                newImageContainer.className = 'variant-dropdown__option-image';
                const newImage = document.createElement('img');
                newImage.src = imageUrl;
                newImage.alt = optionValue;
                newImage.width = 73;
                newImage.height = 60;
                newImageContainer.appendChild(newImage);
                option.insertBefore(newImageContainer, option.querySelector('.variant-dropdown__option-content'));
              }
            }
          }
        });
      });
      
      variantDropdowns.forEach((dropdown) => {
        const optionIndex = parseInt(dropdown.dataset.optionIndex);
        const previouslySelectedValue = selectedValues[optionIndex];
        
        if (previouslySelectedValue) {
          const matchingOption = dropdown.querySelector(`.variant-dropdown__option[data-value="${previouslySelectedValue}"]`);
          
          if (matchingOption) {
            dropdown.querySelectorAll('.variant-dropdown__option').forEach(opt => opt.classList.remove('selected'));
            matchingOption.classList.add('selected');
            
            const selectedText = dropdown.querySelector('.variant-dropdown__selected');
            if (selectedText) {
              selectedText.textContent = previouslySelectedValue;
              selectedText.style.color = '#534949';
            }
            
            this.activeDropdownSelections[optionIndex] = previouslySelectedValue;
          }
        }
      });
      
      const hasAllSelections = variantDropdowns.length > 0 && 
        Array.from(variantDropdowns).every(dropdown => {
          return dropdown.querySelector('.variant-dropdown__option.selected') !== null;
        });
      
      if (hasAllSelections) {
        const finalSelectedValues = {};
        variantDropdowns.forEach((dropdown) => {
          const optionIndex = parseInt(dropdown.dataset.optionIndex);
          const selectedOption = dropdown.querySelector('.variant-dropdown__option.selected');
          if (selectedOption) {
            finalSelectedValues[optionIndex] = selectedOption.dataset.value;
          }
        });
        
        const allOptions = Array.from(variantSelector.querySelectorAll('[data-variant-option]')).filter((option) => {
          const optionWay = option.dataset.variantOption;
          if (!optionWay) return false;
          const optionWayArray = optionWay.split(',');
          return optionWayArray[colorOptionIndex] === selectedColor;
        });
        
        const matchingVariant = allOptions.find((option) => {
          const optionWay = option.dataset.variantOption;
          if (!optionWay) return false;
          const optionWayArray = optionWay.split(',');
          
          let matches = true;
          Object.keys(finalSelectedValues).forEach((index) => {
            const idx = parseInt(index);
            if (optionWayArray[idx] !== finalSelectedValues[index]) {
              matches = false;
            }
          });
          
          return matches;
        });
        
        if (matchingVariant && matchingVariant.value !== variantSelector.selectedVariant) {
          matchingVariant.checked = true;
          variantSelector.selectedVariant = matchingVariant.value;
          variantSelector.eventDispatch();
        }
      }
      
      this.setDropdownsDisabled(false);
    } catch (error) {
      if (error.name !== 'AbortError') {
      }
      this.setDropdownsDisabled(false);
    } finally {
      if (this.currentDropdownUpdateController === signal) {
        this.currentDropdownUpdateController = null;
      }
    }
  }

  findColorOptionIndex(variantSelector) {
    const colorHeads = variantSelector.querySelectorAll('[data-variant-head]');
    if (colorHeads.length > 0) {
      return parseInt(colorHeads[0].dataset.headPosition) || 0;
    }
    return 0;
  }

  findVariantIdForOption(variants, selectedColor, colorOptionIndex, targetOptionIndex, targetOptionValue) {
    for (const variant of variants) {
      const variantOptions = variant.options || [];
      const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                        (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                        variantOptions[0] === selectedColor || 
                        variantOptions[1] === selectedColor;
      
      if (colorMatch && variantOptions[targetOptionIndex] === targetOptionValue) {
        return variant.id.toString();
      }
    }
    return null;
  }

  findVariantIdForOptionWithContext(variants, selectedColor, colorOptionIndex, targetOptionIndex, targetOptionValue, selectedValues) {
    for (const variant of variants) {
      const variantOptions = variant.options || [];
      
      const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                        (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                        variantOptions[0] === selectedColor || 
                        variantOptions[1] === selectedColor;
      
      if (!colorMatch || variantOptions[targetOptionIndex] !== targetOptionValue) {
        continue;
      }
      
      let matchesOtherSelections = true;
      for (const [index, value] of Object.entries(selectedValues)) {
        const idx = parseInt(index);
        if (idx !== targetOptionIndex && idx !== colorOptionIndex) {
          if (variantOptions[idx] !== value) {
            matchesOtherSelections = false;
            break;
          }
        }
      }
      
      if (matchesOtherSelections) {
        return variant.id.toString();
      }
    }
    
    for (const variant of variants) {
      const variantOptions = variant.options || [];
      const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                        (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                        variantOptions[0] === selectedColor || 
                        variantOptions[1] === selectedColor;
      
      if (colorMatch && variantOptions[targetOptionIndex] === targetOptionValue) {
        return variant.id.toString();
      }
    }
    
    return null;
  }

  async updateAllDropdownImages(selectedVariantId) {
    if (this.currentDropdownUpdateController) {
      this.currentDropdownUpdateController.abort();
    }
    
    this.currentDropdownUpdateController = new AbortController();
    const signal = this.currentDropdownUpdateController.signal;
    
    try {
      const productHandle = this.slider?.dataset.productHandle || 
                           this.dataset.productHandle ||
                           window.location.pathname.split('/products/')[1]?.split('?')[0];
      
      if (!productHandle || !selectedVariantId) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const variantSelector = this.querySelector('variant-selector-secondary');
      if (!variantSelector) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const colorOptionIndex = this.findColorOptionIndex(variantSelector);
      const selectedColorInput = variantSelector.querySelector(`[data-variant-head]:checked[data-head-position="${colorOptionIndex}"]`);
      const selectedColor = selectedColorInput ? selectedColorInput.dataset.variantHead : null;
      
      if (!selectedColor) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const checkedOption = variantSelector.querySelector(`[data-variant-option][value="${selectedVariantId}"]`);
      if (!checkedOption) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const optionWay = checkedOption.dataset.variantOption;
      if (!optionWay) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const selectedValuesArray = optionWay.split(',');
      const variantColor = selectedValuesArray[colorOptionIndex];
      
      if (variantColor !== selectedColor) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const variantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
      const selectedValues = {};
      selectedValuesArray.forEach((value, index) => {
        if (index !== colorOptionIndex) {
          selectedValues[index] = value;
        }
      });
      
      const productData = await this.getProductData(productHandle, signal);
      
      if (!productData || !productData.variants || signal.aborted) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const selectedVariant = productData.variants.find(v => v.id.toString() === selectedVariantId);
      if (!selectedVariant) {
        this.setDropdownsDisabled(false);
        return;
      }
      
      const baseProductPrice = productData.price_min || productData.variants[0]?.price || 0;
      
      const formatMoney = (cents) => {
        if (!cents || cents === 0) return '';
        let formatted = '';
        if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
          formatted = theme.Currency.formatMoney(cents, theme.settings?.moneyFormat);
        } else {
          const amount = (cents / 100).toFixed(2);
          const currencyMeta = document.querySelector('meta[property="og:price:currency"]');
          const currencyCode = currencyMeta?.getAttribute('content') || 'GBP';
          const currencySymbol = currencyCode === 'USD' ? '$' : '£';
          formatted = currencySymbol + amount;
        }
        
        if (formatted.includes('<')) {
          const temp = document.createElement('div');
          temp.innerHTML = formatted;
          return temp.textContent || temp.innerText || formatted;
        }
        return formatted;
      };
      
      const variantDataMap = new Map();
      const formatImageUrl = (image) => {
        if (!image) return null;
        const url = typeof image === 'string' ? image : (image.src || image);
        if (!url) return null;
        if (url.startsWith('//')) return 'https:' + url;
        if (url.startsWith('/')) return window.location.origin + url;
        return url;
      };
      
      productData.variants.forEach((variant) => {
        const variantOptions = variant.options || [];
        const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                          (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                          variantOptions[0] === selectedColor || 
                          variantOptions[1] === selectedColor;
        
        if (colorMatch) {
          const variantId = variant.id.toString();
          const variantImage = variant.featured_image 
            ? formatImageUrl(variant.featured_image)
            : (variant.featured_media?.preview_image ? formatImageUrl(variant.featured_media.preview_image) : null);
          
          const priceDifference = variant.price - baseProductPrice;
          const formattedPriceDiff = priceDifference > 0 ? formatMoney(priceDifference) : '';
          
          variantDataMap.set(variantId, {
            image: variantImage,
            price: variant.price || 0,
            priceDifference: priceDifference,
            formattedPriceDiff: formattedPriceDiff,
            comparePrice: variant.compare_at_price || 0
          });
        }
      });
      
      variantDropdowns.forEach((dropdown) => {
        const optionIndex = parseInt(dropdown.dataset.optionIndex) || 0;
        const options = dropdown.querySelectorAll('.variant-dropdown__option');
        
        options.forEach((option) => {
          const optionValue = option.dataset.value;
          if (!optionValue) return;
          
          const variantIdWithContext = this.findVariantIdForOptionWithContext(
            productData.variants,
            selectedColor,
            colorOptionIndex,
            optionIndex,
            optionValue,
            selectedValues
          );
          
          const variantIdForImage = this.findVariantIdForOption(
            productData.variants,
            selectedColor,
            colorOptionIndex,
            optionIndex,
            optionValue
          );
          
          if (!variantIdWithContext) return;
          
          option.dataset.variantId = variantIdWithContext;
          
          const basePricesForOption = [];
          productData.variants.forEach((variant) => {
            const variantOptions = variant.options || [];
            const colorMatch = (colorOptionIndex === 0 && variantOptions[0] === selectedColor) ||
                              (colorOptionIndex === 1 && variantOptions[1] === selectedColor) ||
                              variantOptions[0] === selectedColor || 
                              variantOptions[1] === selectedColor;
            
            if (colorMatch) {
              if (variantOptions[optionIndex] === optionValue) {
                return; 
              }
              
              let matchesPreviousSelections = true;
              Object.keys(selectedValues).forEach((idx) => {
                const index = parseInt(idx);
                if (index !== optionIndex && index !== colorOptionIndex) {
                  if (variantOptions[index] !== selectedValues[index]) {
                    matchesPreviousSelections = false;
                  }
                }
              });
              
              if (matchesPreviousSelections) {
                basePricesForOption.push(variant.price);
              }
            }
          });
          const minPriceForOption = basePricesForOption.length > 0 ? Math.min(...basePricesForOption) : baseProductPrice;
          
          const variantDataForPrice = variantDataMap.get(variantIdWithContext);
          if (variantDataForPrice) {
            const formattedPrice = formatMoney(variantDataForPrice.price);
            if (formattedPrice) option.dataset.price = formattedPrice;
            if (variantDataForPrice.comparePrice) {
              const formattedComparePrice = formatMoney(variantDataForPrice.comparePrice);
              option.dataset.comparePrice = formattedComparePrice;
            }
            
            const incrementalDifference = variantDataForPrice.price - minPriceForOption;
            const formattedIncrementalDiff = incrementalDifference > 0 ? formatMoney(incrementalDifference) : '';
            
            const priceElement = option.querySelector('.variant-dropdown__option-price');
            if (priceElement) {
              if (incrementalDifference > 0 && formattedIncrementalDiff) {
                priceElement.textContent = '+' + formattedIncrementalDiff;
                priceElement.style.display = '';
              } else {
                priceElement.style.display = 'none';
              }
            }
          }
          
          if (variantIdForImage) {
            const variantDataForImage = variantDataMap.get(variantIdForImage);
            if (variantDataForImage && variantDataForImage.image) {
              let imageUrl = variantDataForImage.image;
              if (!imageUrl.includes('width=')) {
                imageUrl = imageUrl.replace(/\?.*$/, '') + '?width=73';
              }
              
              const imageContainer = option.querySelector('.variant-dropdown__option-image');
              if (imageContainer) {
                let currentImage = imageContainer.querySelector('img');
                if (!currentImage) {
                  currentImage = document.createElement('img');
                  currentImage.width = 73;
                  currentImage.height = 60;
                  currentImage.alt = optionValue;
                  imageContainer.appendChild(currentImage);
                }
                if (currentImage.src !== imageUrl) {
                  currentImage.src = imageUrl;
                  currentImage.alt = optionValue;
                }
              } else {
                const newImageContainer = document.createElement('div');
                newImageContainer.className = 'variant-dropdown__option-image';
                const newImage = document.createElement('img');
                newImage.src = imageUrl;
                newImage.alt = optionValue;
                newImage.width = 73;
                newImage.height = 60;
                newImageContainer.appendChild(newImage);
                option.insertBefore(newImageContainer, option.querySelector('.variant-dropdown__option-content'));
              }
            }
          }
        });
      });
      
      this.setDropdownsDisabled(false);
    } catch (error) {
      if (error.name !== 'AbortError') {
      }
      this.setDropdownsDisabled(false);
    } finally {
      if (this.currentDropdownUpdateController === signal) {
        this.currentDropdownUpdateController = null;
      }
    }
  }

  setDropdownsDisabled(disabled) {
    this.isUpdatingImages = disabled;
    const variantDropdowns = this.querySelectorAll('custom-dropdown.variant-dropdown:not([data-type="upsell"])');
    variantDropdowns.forEach((dropdown) => {
      if (disabled) {
        dropdown.dataset.disabled = 'true';
        dropdown.close();
      } else {
        delete dropdown.dataset.disabled;
      }
    });
    
    const colorOptions = this.querySelectorAll('.option-value--color');
    colorOptions.forEach((colorOption) => {
      const input = colorOption.querySelector('input[type="radio"]');
      if (input) {
        if (disabled) {
          input.disabled = true;
          colorOption.dataset.disabled = 'true';
        } else {
          input.disabled = false;
          delete colorOption.dataset.disabled;
        }
      }
    });
  }

  async updateVariantImages(variantId) {
    if (this.currentImageFetchController) {
      this.currentImageFetchController.abort();
    }
    
    this.currentImageFetchController = new AbortController();
    const signal = this.currentImageFetchController.signal;
    
    const trackContainer = this.slider?.querySelector('.blaze-track-container');
    const paginationContainer = this.slider?.querySelector('.main-product-secondary__pagination');
    const removeLoading = () => {
      trackContainer?.classList.remove('loading');
      paginationContainer?.classList.remove('loading');
    };
    
    this.setDropdownsDisabled(true);
    trackContainer?.classList.add('loading');
    paginationContainer?.classList.add('loading');
    
    try {
      const productHandle = this.slider?.dataset.productHandle || 
                           this.dataset.productHandle ||
                           window.location.pathname.split('/products/')[1]?.split('?')[0];
      
      if (productHandle) {
        await this.fetchAndUpdateImages(productHandle, variantId, signal);
      } else {
        removeLoading();
        this.setDropdownsDisabled(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
      }
      removeLoading();
      this.setDropdownsDisabled(false);
    } finally {
      if (this.currentImageFetchController === signal) {
        this.currentImageFetchController = null;
      }
    }
  }

  async getProductData(productHandle, signal, forceRefresh = false) {
    if (!forceRefresh && this.productDataCache && this.productHandleCache === productHandle) {
      return this.productDataCache;
    }
    
    const productJsonUrl = `/products/${productHandle}.js`;
    const fetchOptions = {};
    if (signal) {
      fetchOptions.signal = signal;
    }
    
    const productResponse = await fetch(productJsonUrl, fetchOptions);
    
    if (signal && signal.aborted) {
      return null;
    }
    
    const productData = await productResponse.json();
    
    this.productDataCache = productData;
    this.productHandleCache = productHandle;
    
    return productData;
  }

  async fetchAndUpdateImages(productHandle, variantId, signal) {
    const productData = await this.getProductData(productHandle, signal);
    
    if ((signal && signal.aborted) || !productData) {
      return;
    }
    
    const variant = productData.variants?.find(v => v.id.toString() === variantId);
    
    if (!variant) {
      await this.fetchAndUpdateImagesFromHTML(productHandle, variantId, signal);
      return;
    }
    
    await this.updateImagesFromVariantData(variant, signal);
  }

  async fetchAndUpdateImagesFromHTML(productHandle, variantId, signal) {
    const url = `/products/${productHandle}?variant=${variantId}`;
    let response;
    
    try {
      response = await fetch(url, { signal });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      throw error;
    }
    
    if (signal.aborted) {
      return;
    }
    
    const html = await response.text();
    
    if (signal.aborted) {
      return;
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newSlider = doc.querySelector('main-product-secondary-slider');
    if (!newSlider || !this.slider) {
      if (!signal.aborted) {
        this.setDropdownsDisabled(false);
      }
      return;
    }

    if (signal.aborted) {
      return;
    }

    const newTrack = newSlider.querySelector('.blaze-track');
    const newPagination = newSlider.querySelector('.main-product-secondary__pagination');
    
    if (newTrack && this.slider.blazeTrack) {
      this.slider.blazeTrack.innerHTML = newTrack.innerHTML;
    }
    
    if (newPagination && this.slider.blazePaginationContainer) {
      this.slider.blazePaginationContainer.innerHTML = newPagination.innerHTML;
    }

    this.slider.paginationItems = this.slider.querySelectorAll('.blaze-pagination-item');
    this.slider.totalSlides = this.slider.paginationItems.length;
    
    this.slider.addIndexesToItems();
    this.slider.loadInitialData();
    this.slider.reinitializeSlider();
    this.slider.setupPagination();
    this.slider.updatePagination(0, false);
    this.slider.updatePaginationText(0);
    this.slider.updateArrows();
    
    if (signal.aborted) {
      return;
    }
    
    await this.waitForImagesToLoad();
  }

  async updateImagesFromVariantData(variant, signal) {
    const productHandle = this.slider?.dataset.productHandle || 
                         this.dataset.productHandle ||
                         window.location.pathname.split('/products/')[1]?.split('?')[0];
    
    if (productHandle) {
      await this.fetchAndUpdateImagesFromHTML(productHandle, variant.id.toString(), signal);
    }
  }

  async waitForImagesToLoad() {
    const removeLoading = () => {
      this.slider?.querySelector('.blaze-track-container')?.classList.remove('loading');
      this.slider?.querySelector('.main-product-secondary__pagination')?.classList.remove('loading');
      this.setDropdownsDisabled(false);
    };
    
    const images = this.slider.blazeTrack?.querySelectorAll('img');
    const paginationImages = this.slider.blazePaginationContainer?.querySelectorAll('img');
    const allImages = [...(images || []), ...(paginationImages || [])];
    
    if (allImages.length > 0) {
      const firstImagePromise = new Promise((resolve) => {
        const loadedImages = new Set();
        allImages.forEach((img) => {
          if (img.complete) {
            loadedImages.add(img);
            if (loadedImages.size === 1) resolve();
          } else {
            img.onload = () => {
              loadedImages.add(img);
              if (loadedImages.size === 1) resolve();
            };
            img.onerror = () => {
              loadedImages.add(img);
              if (loadedImages.size === 1) resolve();
            };
          }
        });
        setTimeout(resolve, 500);
      });
      
      await firstImagePromise;
    }
    
    removeLoading();
  }
}

if (customElements.get('main-product-secondary') === undefined) {
  customElements.define('main-product-secondary', MainProductSecondary);
}

